import {
  MultiStepForm,
  type StepConfig,
} from '@/components/ui/multi-step-form';
import {
  useConfigureMetaIntegration,
  useGetMetaIntegration,
  useInitiateMetaAdsAuth,
} from '@/features/integrations/api';
import { pickAutoConnect } from '@/features/integrations/components/meta-ads-wizard-modal';
import { UnlinkMetaButton } from '@/features/integrations/components/unlink-meta-button';
import { META_CONNECT_SELECTION_DIALOG_FLAG } from '@/features/integrations/feature-flags';
import { useMetaLoginForBusiness } from '@/features/integrations/hooks/use-meta-login-for-business';
import { ROUTES } from '@/lib/route-paths';
import { useFlagStatus } from '@/lib/use-flag-status';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { Step1SelectAdAccount } from './step-1-select-ad-account';
import { Step2SelectPage } from './step-2-select-page';
import { Step3EnableChatbot } from './step-3-enable-chatbot';
import { Step4Confirm } from './step-4-confirm';

const metaAdsSetupSchema = z.object({
  adAccountIds: z
    .array(z.string())
    .min(1, 'Please select at least one ad account'),
  pageIds: z
    .array(z.string())
    .min(1, 'Please select at least one Facebook page'),
  enableChatbot: z.boolean(),
});

export type MetaAdsSetupFormData = z.infer<typeof metaAdsSetupSchema>;

const adAccountStepSchema = metaAdsSetupSchema.pick({ adAccountIds: true });
const pageStepSchema = metaAdsSetupSchema.pick({ pageIds: true });
const chatbotStepSchema = z.object({});
const confirmStepSchema = z.object({});

const defaultValues: MetaAdsSetupFormData = {
  adAccountIds: [],
  pageIds: [],
  enableChatbot: true,
};

export type MetaAdsSetupFormProps = {
  returnTo?: string;
};

export function MetaAdsSetupForm({ returnTo }: MetaAdsSetupFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    integration,
    isLoading,
    isPendingConfiguration,
    availableAdAccounts,
    availablePages,
    refetch,
  } = useGetMetaIntegration();

  const { configureMetaAsync } = useConfigureMetaIntegration();

  const { initiateAuth } = useInitiateMetaAdsAuth({
    returnTo: returnTo
      ? `/connect/meta-ads?returnTo=${encodeURIComponent(returnTo)}`
      : '/connect/meta-ads',
  });

  // Gradual migration: when on, reconnect uses the Facebook Login for Business
  // popup (refetch surfaces the refreshed pending integration) instead of the
  // classic redirect. Undefined/false → old path.
  const metaFlfbEnabled = useFeatureFlagEnabled('meta-flfb-login');
  const { launch: launchMetaLogin } = useMetaLoginForBusiness({
    onSuccess: () => {
      void refetch();
    },
  });

  const handleReconnect = useCallback(() => {
    if (metaFlfbEnabled) {
      launchMetaLogin();
    } else {
      initiateAuth();
    }
  }, [metaFlfbEnabled, launchMetaLogin, initiateAuth]);

  const defaultRedirect = returnTo ?? ROUTES.integrations;
  const shouldRedirect =
    !isLoading && (!integration || !isPendingConfiguration);

  useEffect(() => {
    if (shouldRedirect) {
      void navigate({ to: defaultRedirect as never, replace: true });
    }
  }, [shouldRedirect, navigate, defaultRedirect]);

  // With the selection dialog flag off, connect without asking when one ad
  // account is clearly the clinic's (see pickAutoConnect); otherwise, or on a
  // failure (toast from the hook), the form stays up so the owner chooses.
  const selectionDialog = useFlagStatus(META_CONNECT_SELECTION_DIALOG_FLAG);
  const autoChoice =
    !selectionDialog.loading && !selectionDialog.enabled
      ? pickAutoConnect(availableAdAccounts, availablePages, false)
      : null;
  const autoSubmittedRef = useRef(false);
  const [autoSubmitFailed, setAutoSubmitFailed] = useState(false);
  const integrationId = integration?.id;

  useEffect(() => {
    if (
      !autoChoice ||
      !integrationId ||
      !isPendingConfiguration ||
      autoSubmittedRef.current
    )
      return;
    autoSubmittedRef.current = true;
    configureMetaAsync({ integrationId, ...autoChoice })
      .then(() => navigate({ to: defaultRedirect as never }))
      .catch(() => setAutoSubmitFailed(true));
  }, [
    autoChoice,
    integrationId,
    isPendingConfiguration,
    configureMetaAsync,
    navigate,
    defaultRedirect,
  ]);
  const isAutoConnecting =
    selectionDialog.loading || (autoChoice !== null && !autoSubmitFailed);

  const steps = useMemo(() => {
    const result: StepConfig<MetaAdsSetupFormData>[] = [];

    result.push(
      {
        id: 'select-ad-account',
        schema: adAccountStepSchema,
        component: (form) => (
          <Step1SelectAdAccount
            form={form}
            adAccounts={availableAdAccounts}
            onReconnect={handleReconnect}
            onRefresh={refetch}
          />
        ),
      },
      {
        id: 'select-page',
        schema: pageStepSchema,
        component: (form) => (
          <Step2SelectPage
            form={form}
            pages={availablePages}
            onReconnect={handleReconnect}
            onRefresh={refetch}
          />
        ),
      },
      {
        id: 'enable-chatbot',
        schema: chatbotStepSchema,
        component: (form) => <Step3EnableChatbot form={form} />,
      },
      {
        id: 'confirm',
        schema: confirmStepSchema,
        component: (form) => (
          <Step4Confirm
            form={form}
            availableAdAccounts={availableAdAccounts}
            availablePages={availablePages}
          />
        ),
      }
    );

    return result;
  }, [availableAdAccounts, availablePages, handleReconnect, refetch]);

  // Loading state
  if (isLoading || (isPendingConfiguration && isAutoConnecting)) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 py-20">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Loading your Meta integration...
        </p>
      </div>
    );
  }

  // Redirect if no pending integration
  if (shouldRedirect) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 py-20">
        <p className="text-sm text-muted-foreground">Redirecting...</p>
      </div>
    );
  }

  const handleSubmit = async (data: MetaAdsSetupFormData) => {
    if (isSubmitting || !integration?.id) return;
    setIsSubmitting(true);

    try {
      await configureMetaAsync({
        integrationId: integration.id,
        adAccountIds: data.adAccountIds,
        pageIds: data.pageIds,
      });

      void navigate({ to: defaultRedirect as never });
    } catch {
      // Error toast is handled by the hook
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col">
      <MultiStepForm
        steps={steps}
        defaultValues={defaultValues}
        fullSchema={metaAdsSetupSchema}
        onSubmit={handleSubmit}
        submitButtonText={isSubmitting ? 'Connecting...' : 'Connect Meta Ads'}
        continueButtonText="Continue"
        onClose={() => void navigate({ to: defaultRedirect as never })}
      />
      {/* Wrong Facebook login: unlink it and go back to start again. */}
      <div className="flex justify-center pt-4">
        <UnlinkMetaButton
          facebookUserName={integration?.facebookUserName}
          disabled={isSubmitting}
          onUnlinked={() => void navigate({ to: defaultRedirect as never })}
        />
      </div>
    </div>
  );
}
